import { useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { Volume2, VolumeX } from "lucide-react";
import { LogoMarkV2 } from "@/components/v2/LogoV2";
import { playPulse, setSoundPref, soundPref, unlockSound } from "@/lib/introSound";

const SEEN_KEY = "ph_intro_seen";
gsap.config({ nullTargetWarn: false });
const rand = (a, b) => a + Math.random() * (b - a);

export function introMode(force) {
  if (force) return "full";
  if (typeof window === "undefined") return "none";
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return "none";
  try { return sessionStorage.getItem(SEEN_KEY) ? "none" : "full"; } catch { return "full"; }
}

function buildScene(names) {
  const w = window.innerWidth, h = window.innerHeight, mobile = w < 640;
  const low = (navigator.hardwareConcurrency || 8) <= 4 || navigator.connection?.saveData === true;
  const list = names.slice(0, mobile ? 6 : 8);
  const rx = w * (mobile ? 0.3 : 0.32), ry = h * (mobile ? 0.22 : 0.26);
  const brands = list.map((name, i) => {
    const a = (i / list.length) * Math.PI * 2 - Math.PI / 2 + 0.35;
    const stripW = Math.min(w - 40, list.length * (mobile ? 90 : 150));
    return {
      name, x: Math.cos(a) * rx, y: Math.sin(a) * ry,
      sx: -stripW / 2 + (stripW / Math.max(1, list.length - 1)) * i, sy: h * 0.38,
    };
  });
  const stars = Array.from({ length: low ? 16 : mobile ? 26 : 60 }, (_, i) => ({
    key: i, left: `${rand(2, 98)}%`, top: `${rand(2, 98)}%`, size: rand(1, 2.4), delay: `${rand(0, 4).toFixed(2)}s`,
  }));
  return { w, h, brands, stars };
}

export function ConstellationIntro({ names = [], ready = true, force = 0, onExit, onDone }) {
  const [mode] = useState(() => introMode(force));
  const [scene, setScene] = useState(null);
  const [sound, setSound] = useState(soundPref);
  const soundRef = useRef(sound);
  const root = useRef(null);
  const tl = useRef(null);
  const finished = useRef(false);
  const cbs = useRef({ onExit, onDone, names });
  cbs.current = { onExit, onDone, names };

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    try { sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* storage blocked */ }
    cbs.current.onExit?.();
    cbs.current.onDone?.();
  };

  useEffect(() => {
    if (mode === "none") { finish(); return undefined; }
    // Start as soon as brands arrive, or after 700 ms with whatever is available (the wordmark alone is fine).
    const start = () => setScene((s) => s || buildScene(cbs.current.names));
    if (ready) { start(); return undefined; }
    const t = setTimeout(start, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ready]);

  useLayoutEffect(() => {
    if (!scene || !root.current) return undefined;
    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(root.current);
      const logoEl = document.querySelector('[data-testid="logo-home-link"]');
      const r = logoEl?.getBoundingClientRect();
      const tx = r ? r.left + 70 - scene.w / 2 : -scene.w / 2 + 110;
      const ty = r ? r.top + r.height / 2 - scene.h / 2 : -scene.h / 2 + 44;
      const nodes = q(".ph-c-brand");
      const t = gsap.timeline({ onComplete: finish });
      tl.current = t;
      t.fromTo(q(".ph-c-stars"), { opacity: 0 }, { opacity: 1, duration: 0.5, ease: "power1.out" }, 0)
        .fromTo(nodes, { opacity: 0, scale: 0.85, x: (i) => scene.brands[i].x * 1.15, y: (i) => scene.brands[i].y * 1.15 },
          { opacity: 1, scale: 1, x: (i) => scene.brands[i].x, y: (i) => scene.brands[i].y, duration: 0.7, stagger: 0.06, ease: "power2.out" }, 0.2)
        .to(q(".ph-c-trail"), { attr: { "stroke-dashoffset": 0 }, duration: 0.6, stagger: 0.05, ease: "power2.inOut" }, 0.55)
        .fromTo(q(".ph-c-mark"), { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.6, ease: "power2.out" }, 0.75)
        .call(() => { if (soundRef.current) playPulse(); }, null, 0.8)
        .to(q(".ph-c-trail"), { opacity: 0, duration: 0.4 }, 1.45)
        .to(nodes, { x: (i) => scene.brands[i].sx, y: (i) => scene.brands[i].sy, scale: 0.8, duration: 0.7, ease: "power3.inOut" }, 1.5)
        .call(() => cbs.current.onExit?.(), null, 2.2)
        .to(q(".ph-c-mark"), { x: tx, y: ty, scale: 0.45, opacity: 0, duration: 0.75, ease: "power3.inOut" }, 2.15)
        .to(nodes, { opacity: 0, y: "+=24", duration: 0.5, ease: "power2.in" }, 2.3)
        .to(q(".ph-c-sky"), { opacity: 0, duration: 0.7, ease: "power1.inOut" }, 2.25)
        .to(q(".ph-c-stars"), { opacity: 0, duration: 0.5 }, 2.25);
    }, root);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  const skip = () => {
    tl.current?.kill();
    gsap.to(root.current, { opacity: 0, duration: 0.25, onComplete: finish });
  };

  const toggleSound = () => {
    const on = !soundRef.current;
    soundRef.current = on;
    setSound(on);
    setSoundPref(on);
    if (on) { unlockSound(); setTimeout(playPulse, 60); }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") skip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mode === "none") return null;
  const cx = (scene?.w || 0) / 2, cy = (scene?.h || 0) / 2;

  return (
    <div ref={root} className="fixed inset-0 z-[120] overflow-hidden" role="dialog" aria-label="PRICEHUB intro" data-testid="brand-intro-overlay">
      <div className="ph-c-sky absolute inset-0 bg-[#0B1020]" />
      <div className="ph-c-stars absolute inset-0 opacity-0" aria-hidden="true">
        {scene?.stars.map((s) => (
          <span key={s.key} className="ph-star absolute rounded-full bg-white" style={{ left: s.left, top: s.top, width: s.size, height: s.size, animationDelay: s.delay }} />
        ))}
      </div>
      {scene && (
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${scene.w} ${scene.h}`} aria-hidden="true">
          <defs>
            <linearGradient id="ph-trail" x1="0" x2="1"><stop offset="0" stopColor="#6366F1" stopOpacity="0" /><stop offset="1" stopColor="#B6FF5C" stopOpacity="0.85" /></linearGradient>
          </defs>
          {scene.brands.map((b) => (
            <line key={b.name} className="ph-c-trail" x1={cx + b.x} y1={cy + b.y} x2={cx} y2={cy} stroke="url(#ph-trail)" strokeWidth="1.2" pathLength="1" strokeDasharray="1" strokeDashoffset="1" />
          ))}
        </svg>
      )}
      <div className="absolute left-1/2 top-1/2" aria-hidden="true">
        {scene?.brands.map((b) => (
          <span key={b.name} className="ph-c-brand absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-display text-xs font-bold tracking-wide text-[#E2E8F0] opacity-0 sm:text-sm">
            {b.name}
          </span>
        ))}
      </div>
      <div className="ph-c-mark absolute inset-0 flex flex-col items-center justify-center opacity-0">
        <LogoMarkV2 className="h-14 w-14 md:h-16 md:w-16" />
        <p className="mt-4 font-display text-4xl font-extrabold tracking-[-0.03em] text-[#F7F8FC] md:text-6xl">PRICE<span className="text-[#B6FF5C]">HUB</span></p>
        <p className="mt-3 font-mono-ph text-[11px] uppercase tracking-[0.28em] text-[#A5B4FC] md:text-xs" data-testid="brand-intro-tagline">Every brand. Every price list.</p>
      </div>
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2 md:right-8 md:top-8">
        <button type="button" onClick={toggleSound} aria-pressed={sound} aria-label={sound ? "Turn intro sound off" : "Turn intro sound on"} data-testid="intro-sound-toggle"
          className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition-[background-color] hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#A5B4FC]">
          {sound ? <Volume2 className="h-4 w-4 text-[#B6FF5C]" /> : <VolumeX className="h-4 w-4" />}
        </button>
        <button type="button" onClick={skip} autoFocus data-testid="skip-intro-btn"
          className="h-11 rounded-full border border-white/15 bg-white/10 px-5 font-mono-ph text-xs uppercase tracking-[0.18em] text-white transition-[background-color] hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#A5B4FC]">
          Skip intro
        </button>
      </div>
    </div>
  );
}
